// Helper function to build query strings
const buildQueryString = (
    params?: Record<string, string | number | boolean | undefined>
): string => {
    if (!params) {
        return '';
    }

    const entries = Object.entries(params)
        .filter(([_, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`);

    return entries.length > 0 ? `?${entries.join('&')}` : '';
};

// API endpoints configuration
export const ENDPOINTS = {
    // User & Auth
    userInfo: () => 'rest/UserInfo',
    userNames: () => 'rest/Users',
    authenticate: () => 'rest/Authenticate',
    lastSeen: () => 'rest/LastSeen',
    settings: () => 'rest/Settings',

    // Plan & Resources
    planResources: (forecast: number, includeHistory?: { date: string }) =>
        includeHistory
            ? `rest/PlanResources/Forecast/${forecast}/Date/${includeHistory.date}`
            : `rest/PlanResources/Forecast/${forecast}`,
    yearSummary: (year: number | string) => `rest/YearSummary/${year}`,
    personalForecast: (limit?: number) => `rest/PersonalForecast/${limit || 10}`,
    planResourcesAbsent: () => 'rest/PlanResourcesAbsent',
    plan: () => 'rest/Plan',
    planEvent: (date: string) => `rest/Plan/${date}`,
    events: () => 'rest/Events',

    // Categories
    categories: (id?: number) => (id ? `rest/Categories/${id}` : 'rest/Categories'),
    categoriesResources: () => 'rest/CategoriesResources',
    resourcesSettings: () => 'rest/ResourcesSettings',
    categoryRequests: (id?: number) =>
        id ? `rest/CategoryRequests/${id}` : 'rest/CategoryRequests',
    categoryRequestsByStatus: (status: string) => `rest/CategoryRequests/${status}`,

    // Review/manage requests (admins + category responsibles)
    manageCategoryRequests: (id: number) => `rest/ManageCategoryRequests/${id}`,
    manageCategoryRequestsByStatus: (status: string) => `rest/ManageCategoryRequests/${status}`,

    // Change Requests
    changeRequests: () => 'rest/ChangeRequests',
    notifyChange: () => 'rest/NotifyChange',

    // Mail
    mail: (count: number) => `rest/Mail/${count}`,
    attachment: (messageNo: number, section: string, filename: string) =>
        `/rest/Attachment/${messageNo}/${section}/${encodeURI(filename)}`,

    // Agenda
    agenda: (from: string, limit: number) => `rest/Agenda/From/${from}/Limit/${limit}`,

    // Download
    download: (name: string, url: string) =>
        `rest/Download?${new URLSearchParams({ name: btoa(name), url }).toString()}`,

    // Admin
    install: () => 'rest/Install',
    installStep: (step: string) => `rest/Install/${step}`,
    admin: () => 'rest/Admin',
    resources: (id?: number) => (id ? `rest/Resources/${id}` : 'rest/Resources'),
    resourcesCategories: () => 'rest/ResourcesCategories',
    categoriesResponsible: () => 'rest/CategoriesResponsible',
    resourcesProxies: () => 'rest/ResourcesProxies',

    // Logs
    log: (params?: { offset?: number; limit?: number; levels?: string }) =>
        `rest/Log${buildQueryString(params)}`,

    // Metrics
    metrics: (params?: { from?: string; to?: string; resourceId?: number }) =>
        `rest/Metrics${buildQueryString(params)}`,
    metricsEvents: () => 'rest/MetricsEvents',

    // Audit
    eventAudit: (date: string, limit?: number) =>
        `rest/EventAudit/Date/${date}` + buildQueryString({ limit }),
} as const;

export type ApiEndpoints = typeof ENDPOINTS;
